import { memo, useMemo } from 'react';
import { Source, Layer, Marker } from 'react-map-gl/mapbox';
import { Box } from '@mui/material';
import { LoadingToast } from '../../../../components/loading-toast/loading-toast.tsx';
import { GetWetlandsDataResType } from '../../services/wetlands.ts';
import { Position } from '../../types/position.ts';
import { GeoJSON } from 'geojson';

const wetlandsTypes = [
  'Estuarine and Marine Deepwater',
  'Estuarine and Marine Wetland',
  'Freshwater Emergent Wetland',
  'Freshwater Forested/Shrub Wetland',
  'Freshwater Pond',
  'Lake',
  'Riverine',
];

const polygonFillColor = (type: string): string => {
  switch (type) {
    case 'Estuarine and Marine Deepwater':
      return '#007C88';
    case 'Estuarine and Marine Wetland':
      return '#66C2A5';
    case 'Freshwater Emergent Wetland':
      return '#7FC31D';
    case 'Freshwater Forested/Shrub Wetland':
      return '#008837';
    case 'Freshwater Pond':
      return '#688CC0';
    case 'Lake':
      return '#13017C';
    case 'Riverine':
      return '#0190BF';
    default:
      return '#B28653';
  }
};

const getCenter = (coordinates: Position[], yAxis = false) => {
  const sorted = coordinates.map((c) => c[yAxis ? 1 : 0]).sort();
  return (sorted[0] + sorted[sorted.length - 1]) / 2;
};

interface WetlandsFeatureProps {
  feature: GetWetlandsDataResType['features'][number];
  builderViewSelected?: boolean;
}

function WetlandsFeatureBase({
  feature,
  builderViewSelected,
}: WetlandsFeatureProps) {
  const geoData = useMemo(() => {
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [feature.geometry.coordinates[0]],
          },
        },
      ],
    } as GeoJSON;
  }, [feature]);

  const layerPaint = useMemo(() => {
    return {
      'fill-color': polygonFillColor(
        feature.properties['Wetlands.WETLAND_TYPE']
      ),
      'fill-opacity': builderViewSelected ? 0.85 : 0.4,
      'fill-pattern': builderViewSelected
        ? wetlandsTypes.includes(feature.properties['Wetlands.WETLAND_TYPE'])
          ? `stripe-pattern ${feature.properties['Wetlands.WETLAND_TYPE']}`
          : `stripe-pattern Other`
        : '',
    };
  }, [builderViewSelected, feature]);

  const { latitude, longitude } = useMemo(() => {
    return {
      longitude: getCenter(feature.geometry.coordinates[0]),
      latitude: getCenter(feature.geometry.coordinates[0], true),
    };
  }, [feature]);

  const style = useMemo(() => {
    return {
      color: 'white',
      fontSize: '18px',
    };
  }, []);

  return (
    <>
      <Source type="geojson" data={geoData}>
        <Layer id={`data${feature.id}`} type="fill" paint={layerPaint} />
      </Source>
      <Marker longitude={longitude} latitude={latitude} anchor="bottom">
        <Box data-showing-setback={true} style={style}>
          {feature.properties['Wetlands.WETLAND_TYPE']}
        </Box>
      </Marker>
    </>
  );
}

const WetlandsFeature = memo(WetlandsFeatureBase);

interface WetlandsSourceProps {
  wetlandsData: GetWetlandsDataResType;
  wetlandsDataLoading: boolean;
  builderViewSelected?: boolean;
}

const WetlandsSourceBase = ({
  wetlandsData,
  wetlandsDataLoading,
  builderViewSelected = false,
}: WetlandsSourceProps) => {
  return (
    <>
      {/* Wetlands polygons */}
      {!wetlandsDataLoading ? (
        wetlandsData.features.map((feature) => {
          return (
            <WetlandsFeature
              key={feature.id}
              feature={feature}
              builderViewSelected={builderViewSelected}
            />
          );
        })
      ) : (
        <LoadingToast
          message="Loading Wetlands Data..."
          isLoading={wetlandsDataLoading}
          isError={wetlandsData.features.length === 0 && !wetlandsDataLoading}
          isSuccess={wetlandsData.features.length > 0 && !wetlandsDataLoading}
          toastId="wetlands-loading-toast"
        />
      )}
    </>
  );
};

export const WetlandsSource = memo(WetlandsSourceBase);
